const PERSIST_KEY = 'odampersist';
const SESSION_KEY = 'odamsession';

/** Stored values round-trip through JSON, so anything put in here must be serialisable. */
type MemoryBag = Record<string, unknown>;

/**
 * `localStorage.getItem` returns null for a slot that was never written, and
 * `JSON.parse(null)` yields null rather than throwing -- which used to make every
 * read here blow up on `.hasOwnProperty` if `init()` had not run first.
 */
const readBag = (slot: string): MemoryBag => {
  return JSON.parse(localStorage.getItem(slot) || '{}');
};

const writeBag = (slot: string, bag: MemoryBag) => {
  localStorage.setItem(slot, JSON.stringify(bag));
};

export const memory = {
  init: () => {
    // Keep anything already persisted, and start the session bag empty.
    writeBag(PERSIST_KEY, readBag(PERSIST_KEY));
    localStorage.setItem(SESSION_KEY, '{}');
  },

  /**
   * Returns the stored value, or '' when the key is absent.
   *
   * Deliberately `any`: this is the JSON deserialisation boundary, so nothing here
   * can know or check the shape. Callers should assign the result straight into a
   * typed local (see the Saved* types in savefile.ts) so the untyped value stops here.
   */
  get: function (key: string): any {
    const persist = readBag(PERSIST_KEY);
    if (Object.prototype.hasOwnProperty.call(persist, key)) {
      return persist[key];
    }

    const session = readBag(SESSION_KEY);
    if (Object.prototype.hasOwnProperty.call(session, key)) {
      return session[key];
    }

    return '';
  },

  store: function (key: string, value: unknown) {
    const session = readBag(SESSION_KEY);
    session[key] = value;
    writeBag(SESSION_KEY, session);
  },

  persist: function (key: string, value: unknown) {
    const persist = readBag(PERSIST_KEY);
    persist[key] = value;
    writeBag(PERSIST_KEY, persist);
  },

  clear: function () {
    localStorage.setItem(SESSION_KEY, '{}');
    localStorage.setItem(PERSIST_KEY, '{}');
  },
};
